import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { ArrowLeft, Download, Copy, Check, Database, RefreshCw, ShieldOff, Loader2, Monitor, Server } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buildSyncPy } from '@/lib/syncScript';
import NasDockerBundle from '@/components/admin/NasDockerBundle';
import SynologyBackupGuide from '@/components/admin/SynologyBackupGuide';
import { useAuth } from '@/lib/AuthContext';

export default function AdminLocalSync() {
  const { user, isLoadingAuth: authLoading } = useAuth();
  const [endpoint, setEndpoint] = useState('https://motionreality.co.uk/functions/dataSyncExport');
  const [token, setToken] = useState('');
  const [interval, setIntervalSec] = useState(60);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState('pc');

  if (authLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center"><ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p></div>
      </div>
    );
  }

  const script = buildSyncPy(endpoint || 'YOUR_ENDPOINT_URL', token || 'YOUR_SYNC_TOKEN', interval || 60);

  const download = () => {
    const blob = new Blob([script], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sync.py';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-center gap-3 mb-2"><Database className="w-6 h-6 text-primary" /><p className="text-primary text-xs font-bold tracking-widest">SYSTEM &amp; INTEGRATIONS</p></div>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Local Database Sync</h1>
          <p className="text-white/40 text-sm mt-2 max-w-2xl">Run a small script on your PC that polls the website every minute and keeps a local SQLite mirror of your products, orders, customers, bookings and experiences — near real-time.</p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-4xl mx-auto space-y-8">
        {/* Step 1 */}
        <div className="bg-[#111115] border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4"><span className="w-7 h-7 bg-primary text-white font-black text-sm flex items-center justify-center">1</span><h2 className="text-white font-bold text-base">Get your endpoint URL</h2></div>
          <p className="text-white/50 text-sm leading-relaxed">This is pre-filled with your site's <span className="text-white">dataSyncExport</span> endpoint. You can confirm it in <span className="text-white">Dashboard → Code → Functions → <code className="text-primary">dataSyncExport</code></span>, or edit it if your sync runs against a different deployment.</p>
          <div className="mt-4">
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">ENDPOINT URL</Label>
            <Input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://.../functions/dataSyncExport" className="bg-[#0d0d0f] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60" />
          </div>
        </div>

        {/* Step 2 */}
        <div className="bg-[#111115] border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4"><span className="w-7 h-7 bg-primary text-white font-black text-sm flex items-center justify-center">2</span><h2 className="text-white font-bold text-base">Enter your sync token</h2></div>
          <p className="text-white/50 text-sm leading-relaxed">This is the <span className="text-white">DATA_SYNC_TOKEN</span> value you set in App Settings → Secrets. The script sends it to authenticate.</p>
          <div className="mt-4">
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SYNC TOKEN</Label>
            <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="••••••••••••••••" className="bg-[#0d0d0f] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60" />
          </div>
          <div className="mt-4">
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SYNC INTERVAL (seconds)</Label>
            <Input type="number" min="10" value={interval} onChange={(e) => setIntervalSec(Number(e.target.value))} className="bg-[#0d0d0f] border-white/10 text-white h-11 focus:border-primary/60" />
          </div>
        </div>

        {/* Step 3 */}
        <div className="bg-[#111115] border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4"><span className="w-7 h-7 bg-primary text-white font-black text-sm flex items-center justify-center">3</span><h2 className="text-white font-bold text-base">Choose where to run it</h2></div>
          <div className="flex items-center gap-2 mb-5">
            <button onClick={() => setMode('pc')} className={`flex items-center gap-2 text-xs font-bold tracking-wider px-4 py-2.5 border transition-colors ${mode === 'pc' ? 'border-primary/50 text-primary bg-primary/5' : 'border-white/10 text-white/50 hover:text-white'}`}>
              <Monitor className="w-4 h-4" /> PC (PYTHON)
            </button>
            <button onClick={() => setMode('docker')} className={`flex items-center gap-2 text-xs font-bold tracking-wider px-4 py-2.5 border transition-colors ${mode === 'docker' ? 'border-primary/50 text-primary bg-primary/5' : 'border-white/10 text-white/50 hover:text-white'}`}>
              <Server className="w-4 h-4" /> SYNOLOGY NAS
            </button>
          </div>

          {mode === 'pc' ? (
            <>
              <p className="text-white/50 text-sm leading-relaxed">Requires <span className="text-white">Python 3.8+</span> (no extra packages). Save the file, then run it in a terminal:</p>
              <div className="flex items-center gap-3 mt-4">
                <button onClick={download} disabled={!endpoint || !token}
                  className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors disabled:opacity-40">
                  <Download className="w-4 h-4" /> DOWNLOAD sync.py
                </button>
                <button onClick={copy} disabled={!endpoint || !token}
                  className="flex items-center gap-2 border border-white/10 text-white/60 hover:text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors disabled:opacity-40">
                  {copied ? <><Check className="w-4 h-4 text-primary" /> COPIED</> : <><Copy className="w-4 h-4" /> COPY SCRIPT</>}
                </button>
              </div>
              <pre className="mt-4 bg-[#0d0d0f] border border-white/5 p-4 text-white/70 text-xs overflow-x-auto"><code>python sync.py --loop</code></pre>
              <p className="text-white/40 text-xs mt-3 flex items-start gap-2"><RefreshCw className="w-3.5 h-3.5 mt-0.5 shrink-0" /> A <code className="text-white/60">data/motion_reality.db</code> file appears next to <code className="text-white/60">sync.py</code> and refreshes automatically every {interval}s. If nothing shows up, check <code className="text-white/60">sync.log</code> (next to <code className="text-white/60">sync.py</code>) for the exact error. Open the db with any SQLite viewer (e.g. DB Browser for SQLite). Drop <code className="text-white/60">--loop</code> to do a one-off sync instead.</p>
            </>
          ) : (
            <NasDockerBundle endpoint={endpoint} token={token} interval={interval} />
          )}
        </div>

        <div className="bg-primary/5 border border-primary/20 p-4">
          <p className="text-white/60 text-xs leading-relaxed">
            <span className="text-primary font-bold">What gets mirrored:</span> Products, Categories, Orders, Customers, Bookings, Experiences, Rigs, Reviews, Feedback, Gift Cards, Discount Codes, Shipping Rates, CRM tasks/tags, and more — each in its own table, keyed by record ID.
          </p>
          <p className="text-white/60 text-xs leading-relaxed mt-2">
            <span className="text-primary font-bold">Queryable, not just stored:</span> each table is written with real named columns (name, price, status, stock_quantity…) so you can sort &amp; filter it directly, plus a <code className="text-white/80">_raw</code> column holding the full JSON for any field not promoted. After every sync the script also rebuilds <span className="text-white">report views</span> you can open instantly — <code className="text-white/80">v_low_stock</code>, <code className="text-white/80">v_top_customers</code>, <code className="text-white/80">v_orders_by_status</code>, <code className="text-white/80">v_orders_this_month</code>, <code className="text-white/80">v_bookings_by_rig</code>, <code className="text-white/80">v_gift_cards_outstanding</code>, <code className="text-white/80">v_reviews_pending</code> and more.
          </p>
        </div>

        {/* Dedicated Synology NAS automated backup guide */}
        <SynologyBackupGuide />
      </section>
    </div>
  );
}